import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-bold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-md hover:bg-primary-hover hover:shadow-lg hover:-translate-y-0.5 theme-transition text-sm font-bold border border-primary hover:border-primary-hover",
        destructive:
          "bg-destructive text-destructive-foreground shadow-md hover:bg-destructive/90 hover:shadow-lg hover:-translate-y-0.5 text-sm font-bold border border-destructive",
        outline:
          "border-2 border-border bg-background text-foreground shadow-sm hover:bg-primary hover:text-primary-foreground hover:shadow-md hover:scale-[1.02] theme-transition text-sm font-bold hover:border-primary",
        secondary:
          "bg-secondary text-secondary-foreground shadow-md hover:bg-secondary-hover hover:shadow-lg hover:scale-[1.02] theme-transition text-sm font-bold border border-secondary hover:border-secondary-hover",
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] theme-transition text-sm font-semibold border-2 border-transparent hover:border-accent",
        link: "text-primary underline-offset-4 hover:underline hover:scale-[1.02] theme-transition text-sm font-semibold",
      },
      size: {
        default: "h-9 px-4 py-2 text-sm",
        sm: "h-8 rounded-md px-3 text-xs font-bold",
        lg: "h-10 rounded-md px-8 text-base font-bold",
        icon: "h-9 w-9 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
